import { Box, Button, Collapse, Container, Typography } from '@mui/material';
import { ArrowBack, ExpandLess, ExpandMore } from '@mui/icons-material';
import DetailSearch from '@components/search/DetailSearch';
import TranscriptViewController from '@controllers/TranscriptViewController';
import ChapterNavigationController from '@controllers/ChapterNavigationController';
import EmptyState from '@components/shared/EmptyState';
import EpisodeSummaries from '@components/episode/EpisodeSummaries';
import EpisodeThumbnail from '@components/episode/EpisodeThumbnail';
import { getEpisodeNumber } from '@models/episodes/episodeModel';

export default function EpisodeDetailPageView({
  episode,
  episodesLoading,
  episodeDate,
  searchQuery,
  onSearchChange,
  transcriptExpanded,
  onTranscriptToggle,
  onBack,
  transcript,
  loading: transcriptLoading,
  error: transcriptError
}) {
  if (episodesLoading) {
    return <EmptyState type="loading" message="Loading episode..." />;
  }

  if (!episode) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <EmptyState type="error" message="Episode not found." />
        </Box>
      </Container>
    );
  }

  const episodeNumber = getEpisodeNumber(episode.id);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 3, sm: 4 } }}>
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            alignSelf: 'flex-start',
            mb: { xs: 2, sm: 2.5 },
            px: 0.5,
            color: 'text.secondary',
            fontWeight: 600,
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'transparent'
            }
          }}
        >
          Back to episodes
        </Button>

        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            aspectRatio: '16 / 9',
            mx: 'auto',
            mb: { xs: 3, sm: 4 },
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            boxShadow: 2
          }}
        >
          <EpisodeThumbnail
            imageUrl={episode.image}
            alt={`${episode.title} episode artwork`}
            episodeNumber={episodeNumber}
            size="100%"
            loading="eager"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            mb: { xs: 3, sm: 4 },
            gap: 2
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mb: 1,
                fontSize: { xs: 'clamp(1.9rem, 8vw, 2.5rem)', sm: '2.125rem' },
                lineHeight: 1.15,
                overflowWrap: 'anywhere'
              }}
            >
              {episode.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {episodeDate}
            </Typography>
          </Box>
          {episode.hasTranscript && (
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0, minWidth: 0 }}>
              <DetailSearch
                value={searchQuery}
                onChange={onSearchChange}
                onClear={() => onSearchChange('')}
                episodeTitle={episode.title}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <EpisodeSummaries
            summary={episode.summary}
            aiSummary={episode.aiSummary}
          />
        </Box>

        {!episode.hasTranscript ? (
          <EmptyState type="empty" message="Transcript not available for this episode." />
        ) : (
          <Box sx={{ mt: { xs: 3, sm: 4 } }}>
            <Button
              variant="outlined"
              onClick={onTranscriptToggle}
              endIcon={transcriptExpanded ? <ExpandLess /> : <ExpandMore />}
              aria-expanded={transcriptExpanded}
              aria-controls="episode-transcript"
            >
              {transcriptExpanded ? 'Hide transcript' : 'Show transcript'}
            </Button>

            <Collapse
              id="episode-transcript"
              in={transcriptExpanded}
              unmountOnExit
            >
              <Box sx={{ pt: 3 }}>
                {transcriptLoading ? (
                  <EmptyState type="loading" message="Loading transcript..." />
                ) : transcriptError ? (
                  <EmptyState type="error" message={transcriptError} />
                ) : transcript ? (
                  <>
                    {transcript.chapters && transcript.chapters.length > 0 && (
                      <ChapterNavigationController chapters={transcript.chapters} />
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Transcript
                      </Typography>
                      <TranscriptViewController
                        transcript={transcript}
                        searchQuery={searchQuery}
                      />
                    </Box>
                  </>
                ) : null}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Container>
  );
}
